import { cn } from '@/shared/lib/cn';
import { Fragment, type ReactNode } from 'react';

/**
 * A bolded run that is nothing but a signed number, optionally parenthesized
 * and optionally a percentage: `+12.2%`, `-5.2`, `(+12.2%)`, `-$1,200`.
 */
const SIGNED_DELTA = /^\(?[+−-]\s?\$?[\d.,]+%?\)?$/;

const BOLD_RUN = /\*\*(.+?)\*\*/g;

type RenderOptions = {
  /**
   * Color signed deltas green (positive) or red (negative).
   *
   * On in the card body, off inside the "Why it's happening" box — the design
   * leaves the same figures neutral there (Figma 248:20030), so the eye is
   * drawn to the headline numbers only.
   *
   * Sentiment is read from the sign alone. The response contract carries no
   * per-figure sentiment, so an unsigned phrase the designer tinted red (e.g.
   * "40 fewer beds") renders neutral. Logged as an open question on the plan.
   */
  colorDeltas?: boolean;
};

/**
 * Renders the model's `**bold**` markers.
 *
 * The model is told to emit Markdown-style bold and nothing else, so this is a
 * deliberately tiny parser rather than a Markdown dependency — anything it does
 * not recognize is shown verbatim rather than swallowed.
 */
export const renderRichText = (
  text: string,
  { colorDeltas = false }: RenderOptions = {}
): ReactNode[] => {
  const nodes: ReactNode[] = [];
  let cursor = 0;
  let match: RegExpExecArray | null;

  // Regexes with /g carry state across calls, so this must be per-invocation.
  const pattern = new RegExp(BOLD_RUN.source, 'g');

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > cursor) {
      nodes.push(
        <Fragment key={`t-${cursor}`}>
          {text.slice(cursor, match.index)}
        </Fragment>
      );
    }

    const inner = match[1];
    const trimmed = inner.trim();
    const isDelta = colorDeltas && SIGNED_DELTA.test(trimmed);
    const isNegative = isDelta && /^\(?[−-]/.test(trimmed);

    nodes.push(
      <span
        key={`b-${match.index}`}
        className={cn(
          'font-semibold',
          isDelta &&
            (isNegative ? 'text-insight-priority' : 'text-polar-green-7')
        )}
      >
        {inner}
      </span>
    );

    cursor = match.index + match[0].length;
  }

  if (cursor < text.length) {
    nodes.push(<Fragment key={`t-${cursor}`}>{text.slice(cursor)}</Fragment>);
  }

  return nodes;
};
